export const EXTERN_ROOT_COOKIE_DOMAIN = 'extern.com';

const EXTERN_HOSTNAME_PATTERN = /(^|\.)extern\.com$/i;

export function resolveRootCookieDomain(hostname: string): string | null {
  if (!EXTERN_HOSTNAME_PATTERN.test(hostname)) {
    return null;
  }

  return EXTERN_ROOT_COOKIE_DOMAIN;
}

export function getCookieValue(
  name: string,
  cookieSource = typeof document === 'undefined' ? '' : document.cookie,
): string | null {
  if (!cookieSource) {
    return null;
  }

  const prefix = `${name}=`;
  const match = cookieSource.split('; ').find((row) => row.startsWith(prefix));

  if (!match) {
    return null;
  }

  return decodeURIComponent(match.slice(prefix.length));
}

const EPOCH_HTTP_DATE = 'Thu, 01 Jan 1970 00:00:00 GMT';

export function buildCookieAttributes(options: {
  domain: string | null;
  maxAgeSeconds: number;
  secure?: boolean;
}): string {
  const parts = ['Path=/', `Max-Age=${options.maxAgeSeconds}`, 'SameSite=Lax'];

  // Max-Age alone does not remove a cookie everywhere, so a deletion carries a past Expires as well.
  if (options.maxAgeSeconds === 0) {
    parts.push(`Expires=${EPOCH_HTTP_DATE}`);
  }

  if (options.domain) {
    parts.push(`Domain=${options.domain}`);
  }

  if (options.secure ?? true) {
    parts.push('Secure');
  }

  return parts.join('; ');
}

export function writeRootDomainCookie(options: {
  name: string;
  value: string;
  maxAgeSeconds: number;
  hostname?: string;
}): void {
  if (typeof document === 'undefined') {
    return;
  }

  const hostname = options.hostname ?? window.location.hostname;
  const domain = resolveRootCookieDomain(hostname);
  const attributes = buildCookieAttributes({
    domain,
    maxAgeSeconds: options.maxAgeSeconds,
    secure: hostname !== 'localhost' && !hostname.endsWith('.local'),
  });

  // First-party analytics cookies must be readable across www/app subdomains.
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not available in all target browsers yet
  document.cookie = `${options.name}=${encodeURIComponent(options.value)}; ${attributes}`;
}

export function clearRootDomainCookie(options: { name: string; hostname?: string }): void {
  writeRootDomainCookie({
    name: options.name,
    value: '',
    maxAgeSeconds: 0,
    hostname: options.hostname,
  });
}
